import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import type { AdminLoginOut } from '@/lib/api/types';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

const schema = z.object({
  email: z.string().email('Enter a valid email address.'),
  password: z.string().min(12, 'Password must be at least 12 characters.').max(256),
});

type FormValues = z.infer<typeof schema>;

interface AdminLoginIn {
  email: string;
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);
  const setRequires2fa = useAuthStore((s) => s.setRequires2fa);
  const setChallengeToken = useAuthStore((s) => s.setChallengeToken);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: FormValues) {
    setSubmitting(true);
    try {
      const body: AdminLoginIn = { email: values.email.trim(), password: values.password };
      const res = await apiClient.post<AdminLoginOut>('/auth/admin/login', body);
      const data = res.data;

      // 2FA gate: backend withholds tokens and hands back a short-lived
      // challenge token instead. Stash it and route to the TOTP screen —
      // no session is established until /auth/2fa/login succeeds.
      if (data.requires_2fa && data.challenge_token) {
        setChallengeToken(data.challenge_token);
        setRequires2fa(true);
        navigate('/login/2fa', { replace: true });
        return;
      }

      if (!data.access_token || !data.refresh_token || !data.user) {
        toast.error('Could not sign in', {
          description: 'Unexpected login response from the backend.',
        });
        return;
      }

      // A non-2FA success after a prior 2FA attempt (e.g. switching
      // accounts) must clear any stale challenge, or the guard would strand
      // this session on the TOTP screen.
      setRequires2fa(false);
      setChallengeToken(null);
      setTokens(data.access_token, data.refresh_token);
      setUser({
        id: data.user.id,
        phone: data.user.phone,
        email: data.user.email,
        role: data.user.role,
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data
        ?.error?.code;
      // Sentry SDK (BE-113.1 follow-up) will capture the underlying
      // axios error automatically once VITE_SENTRY_DSN is set; until then
      // the toast below is the only diagnostic the operator sees.
      if (status === 401 || code === 'INVALID_CREDENTIALS') {
        toast.error('Wrong email or password.');
      } else if (status === 429) {
        toast.error('Too many attempts — try again in an hour.');
      } else {
        toast.error('Could not sign in', {
          description: 'Backend may be offline. Check VITE_API_BASE_URL.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader className="space-y-2 text-center">
        <CardTitle className="text-2xl">Sign in to digipop admin</CardTitle>
        <CardDescription>
          Internal-only console. 2FA in production. IP allowlisted in production.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="username"
                      placeholder="makie@digipop.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••••••"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Single-step sign-in for admin users. Merchants use phone OTP.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
